import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { validateToken } from 'utils/authUtils'
import { resetStore } from 'app/store'
import {
  ACCESS_TOKEN_KEY,
  clearAuthTokens,
  getAccessToken,
  getStoredAuth,
  getUserId,
  persistUserId,
  resolveUserIdFromPayload,
  setAuthTokens
} from 'lib/auth/tokenStorage'

const AuthContext = createContext(null)

const EMPTY_AUTH = Object.freeze({
  accessToken: null,
  refreshToken: null,
  expiresIn: null,
  userId: null,
  isAuthenticated: false
})

const PUBLIC_PREFIX = '/public/'
const NO_REDIRECT_PATHS = new Set(['/', '/login', '/forgot-password'])

const toAuthState = stored => ({
  accessToken: stored.accessToken,
  refreshToken: stored.refreshToken,
  expiresIn: stored.expiresIn,
  userId: stored.userId,
  isAuthenticated: Boolean(stored.accessToken)
})

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()

  // Treat /login and /public/* as public
  const isPublicPath =
    location.pathname === '/login' ||
    location.pathname.startsWith(PUBLIC_PREFIX)

  const hasToken = getAccessToken()

  // On public path with no token → don't show loading spinner
  const [loading, setLoading] = useState(!isPublicPath || !!hasToken)
  const [authData, setAuthData] = useState(EMPTY_AUTH)

  const redirectToLogin = useCallback(() => {
    // Never redirect from public paths
    if (isPublicPath) return

    // Don't redirect from these special paths
    if (NO_REDIRECT_PATHS.has(location.pathname)) return

    const currentPath = location.pathname + location.search
    const shouldPreserve =
      currentPath &&
      !NO_REDIRECT_PATHS.has(currentPath) &&
      !currentPath.startsWith(PUBLIC_PREFIX)

    if (shouldPreserve) {
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`, {
        replace: true
      })
    } else {
      navigate('/login', { replace: true })
    }
  }, [isPublicPath, location.pathname, location.search, navigate])

  const clearTokens = useCallback(() => {
    clearAuthTokens()
    setAuthData(EMPTY_AUTH)
    resetStore()
  }, [])

  const setTokens = useCallback(data => {
    try {
      setAuthTokens(data)
      setAuthData(toAuthState(getStoredAuth()))
    } catch (error) {
      console.error('Error setting tokens:', error)
    }
  }, [])

  const syncTokensFromStorage = useCallback(() => {
    const stored = getStoredAuth()
    if (stored.accessToken) {
      setAuthData(toAuthState(stored))
    }
  }, [])

  const loadAuthData = useCallback(async () => {
    const { accessToken, refreshToken, expiresIn, userId } = getStoredAuth()

    const isPublic =
      location.pathname === '/login' ||
      location.pathname.startsWith(PUBLIC_PREFIX)

    if (isPublic) {
      setAuthData(EMPTY_AUTH)
      setLoading(false)
      return
    }

    setLoading(true)

    try {
      if (!accessToken) {
        setAuthData(EMPTY_AUTH)
        redirectToLogin()
        return
      }

      const response = await validateToken(accessToken)
      if (!response?.success || response?.status === false) {
        clearTokens()
        if (!NO_REDIRECT_PATHS.has(location.pathname)) {
          toast.error('Session expired, please login again')
        }
        redirectToLogin()
        return
      }

      persistUserId(resolveUserIdFromPayload(response) || userId)

      if (response.token_rotated && response.access_token) {
        setTokens({
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          expires_in: response.expires_in,
          user: response.user || response.current_user,
          user_id: response.user_id,
          agent_id: response.agent_id
        })

        if (!getUserId()) {
          clearTokens()
          if (!NO_REDIRECT_PATHS.has(location.pathname)) {
            toast.error('Session expired, please login again')
          }
          redirectToLogin()
        }
        return
      }

      const resolvedUserId = getUserId()
      if (!resolvedUserId) {
        clearTokens()
        if (!NO_REDIRECT_PATHS.has(location.pathname)) {
          toast.error('Session expired, please login again')
        }
        redirectToLogin()
        return
      }

      setAuthData({
        accessToken,
        refreshToken,
        expiresIn,
        userId: resolvedUserId,
        isAuthenticated: true
      })
    } catch (error) {
      console.error('Token validation failed:', error)
      clearTokens()
      if (!NO_REDIRECT_PATHS.has(location.pathname)) {
        toast.error('Session expired, please login again')
      }
      redirectToLogin()
    } finally {
      setLoading(false)
    }
  }, [clearTokens, redirectToLogin, setTokens])

  useEffect(() => {
    loadAuthData()
  }, [])

  useEffect(() => {
    const onStorage = event => {
      if (event.key !== ACCESS_TOKEN_KEY && event.key !== null) {
        return
      }

      if (window.location.pathname.startsWith(PUBLIC_PREFIX)) {
        return
      }

      if (!getAccessToken()) {
        setAuthData(EMPTY_AUTH)
        resetStore()
        return
      }

      syncTokensFromStorage()
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [syncTokensFromStorage])

  const value = useMemo(
    () => ({
      ...authData,
      loading,
      setTokens,
      clearTokens,
      syncTokensFromStorage
    }),
    [authData, loading, setTokens, clearTokens, syncTokensFromStorage]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
